import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Solutions from './components/Solutions';
import AgribusinessBanner from './components/AgribusinessBanner';
import WhyChooseUs from './components/WhyChooseUs';
import FAQ from './components/FAQ';
import Footer from './components/Footer';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const sections = ['home', 'about', 'services', 'solutions'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const navLinkClass = `text-sm font-medium tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
    isScrolled ? 'text-gray-700 hover:text-[#3782df]' : 'text-white hover:text-white/80'
  }`;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="w-full px-4 sm:px-8 lg:px-20 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/4f08bda5a1b03b110582211ba3bde471.png"
                alt="LumanyiTech Logo"
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className={navLinkClass}>Home</button>
              <Link to="/about" className={navLinkClass}>About</Link>
              <Link to="/services" className={navLinkClass}>Services</Link>
              <Link to="/our-work" className={navLinkClass}>Our Work</Link>
              <Link to="/agribusiness" className={navLinkClass}>Agribusiness</Link>
              <Link to="/contact" className={navLinkClass}>Contact</Link>
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:block">
              <Link
                to="/contact"
                className="px-6 py-2.5 bg-[#3782df] text-white rounded-full text-sm font-medium hover:bg-[#2a6bc7] transition-colors whitespace-nowrap"
              >
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className={`md:hidden mt-2 pb-4 border-t ${isScrolled ? 'border-gray-100 bg-white' : 'border-white/10 bg-black/60 backdrop-blur-sm'}`}>
              <div className="flex flex-col gap-1 pt-3">
                {[
                  { label: 'Home', action: () => scrollToSection('home') },
                ].map(item => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className={`text-left px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${isScrolled ? 'text-gray-700 hover:bg-gray-50 hover:text-[#3782df]' : 'text-white hover:bg-white/10'}`}
                  >
                    {item.label}
                  </button>
                ))}
                {[
                  { label: 'About', to: '/about' },
                  { label: 'Services', to: '/services' },
                  { label: 'Our Work', to: '/our-work' },
                  { label: 'Agribusiness', to: '/agribusiness' },
                  { label: 'Contact', to: '/contact' },
                ].map(item => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${isScrolled ? 'text-gray-700 hover:bg-gray-50 hover:text-[#3782df]' : 'text-white hover:bg-white/10'}`}
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="px-4 pt-2">
                  <Link
                    to="/contact"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full text-center px-6 py-2.5 bg-[#3782df] text-white rounded-full text-sm font-medium hover:bg-[#2a6bc7] transition-colors whitespace-nowrap"
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Sections */}
      <section id="home">
        <Hero onExploreClick={() => scrollToSection('solutions')} />
      </section>

      <section id="about">
        <About />
      </section>

      <section id="services">
        <Services />
      </section>

      <AgribusinessBanner />

      <section id="solutions">
        <Solutions />
      </section>

      <WhyChooseUs />

      <FAQ />

      <Footer />
    </div>
  );
}
